// The paid-credits rule (SCRUM-196, decided 2026-10-18), mirrored from the
// backend credit service so the app never sends a scan the server will refuse.
//
// Premium, hair and makeup are paid-credits-only: on the Free plan only
// purchased (surplus) credits pay for them; Plus/Premium plan credits always
// do. Every other feature can use the whole balance.

import type { Credits, FeatureCost } from '../types/api';

/**
 * The balance shape the rule reads. `costs` may be missing on a partially
 * loaded or older payload; the rule then treats every cost as unknown.
 */
type CreditsLike = Omit<Credits, 'costs'> & { costs?: FeatureCost[] };

/** Why a metered feature can't run with the current balance. */
export type CreditBlock = 'none' | 'paid_credits_required' | 'insufficient';

/** The backend's 402 `code` for a paid-credits-only refusal. */
export const PAID_CREDITS_REQUIRED_CODE = 'paid_credits_required';

export const CREDIT_COPY = {
  paidOnlyNote: 'Runs on purchased credits, or included with Plus & Premium.',
  unlockTitle: 'Unlock this analysis',
  unlockBody:
    'Free credits cover skin and product scans. This one runs on purchased credits, or is included with Plus & Premium.',
  topUpBody: 'Not enough credits for this analysis. Top up now, or upgrade for more monthly credits.',
  unlockedSending: 'Unlocked · sending your scan',
  purchasePending:
    "Purchase received. Your credits appear here as soon as it's confirmed — then tap analyse again.",
} as const;

/** Anything that isn't Plus or Premium. Unknown balance → treated as Free. */
export function isFreePlan(credits: CreditsLike | null | undefined): boolean {
  const plan = (credits?.plan_name ?? '').toLowerCase();
  return !(plan.includes('plus') || plan.includes('premium'));
}

export function isPaidCreditsOnly(credits: CreditsLike | null | undefined, featureKey: string): boolean {
  return credits?.costs?.find((c) => c.feature_key === featureKey)?.paid_credits_only === true;
}

/** Credits that can pay for one use of featureKey. */
export function spendableFor(credits: CreditsLike | null | undefined, featureKey: string): number {
  if (!credits) return 0;
  return isPaidCreditsOnly(credits, featureKey) && isFreePlan(credits)
    ? credits.surplus_balance
    : credits.total;
}

function costOf(credits: CreditsLike | null | undefined, featureKey: string): number | null {
  return credits?.costs?.find((c) => c.feature_key === featureKey)?.credit_cost ?? null;
}

/**
 * Why featureKey can't run. 'none' also when the balance or the cost is not
 * known yet: the server is the real gate, the client only avoids a pointless
 * request.
 */
export function blockFor(credits: CreditsLike | null | undefined, featureKey: string): CreditBlock {
  const cost = costOf(credits, featureKey);
  if (!credits || cost == null) return 'none';
  if (spendableFor(credits, featureKey) >= cost) return 'none';
  return isPaidCreditsOnly(credits, featureKey) && isFreePlan(credits) ? 'paid_credits_required' : 'insufficient';
}

/** Credits still missing for one use (0 when affordable or unknown). */
export function shortfallFor(credits: CreditsLike | null | undefined, featureKey: string): number {
  const cost = costOf(credits, featureKey);
  if (!credits || cost == null) return 0;
  return Math.max(0, cost - spendableFor(credits, featureKey));
}
